import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import { Save } from "lucide-react";

const MODULES = [
  "dashboard", "suppliers", "materials", "templates", "schedules",
  "gate_entry", "grn", "quality", "stock", "outward",
  "users", "roles", "settings", "audit",
];
const ACTIONS = ["view", "create", "edit", "approve", "reject", "print", "export", "admin"];

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [dirty, setDirty] = useState({});

  const load = () => api.get("/roles").then((r) => setRoles(r.data));
  useEffect(() => { load(); }, []);

  const toggle = (role, module, action) => {
    const cur = (dirty[role.id]?.permissions ?? role.permissions) || {};
    const mod = [...(cur[module] || [])];
    const idx = mod.indexOf(action);
    if (idx >= 0) mod.splice(idx, 1); else mod.push(action);
    const newPerms = { ...cur, [module]: mod };
    setDirty({ ...dirty, [role.id]: { ...role, ...(dirty[role.id] || {}), permissions: newPerms } });
  };

  const hasAction = (role, module, action) => {
    const perms = (dirty[role.id]?.permissions ?? role.permissions) || {};
    return (perms[module] || []).includes(action);
  };

  const save = async (role) => {
    const updated = dirty[role.id];
    if (!updated) return;
    try {
      await api.put(`/roles/${role.id}`, {
        name: updated.name,
        description: updated.description || "",
        permissions: updated.permissions,
        is_super_admin: !!updated.is_super_admin,
      });
      toast.success(`Updated ${role.name}`);
      setDirty({ ...dirty, [role.id]: undefined });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="roles-page">
      <PageHeader title="Roles & Permissions" subtitle="Per-module · per-action matrix" />
      <div className="p-8 space-y-8">
        {roles.map((role) => (
          <div key={role.id} className="bg-white border border-[#EEEEEE] rounded-sm" data-testid={`role-${role.name}`}>
            <div className="px-5 py-3 border-b border-[#EEEEEE] flex items-center justify-between bg-[#F5F5F5]">
              <div>
                <div className="text-sm font-bold text-[#0D2B5E]">{role.name} {role.is_super_admin && <span className="text-[10px] text-[#2E7D32] ml-2 uppercase tracking-widest">(Super Admin)</span>}</div>
                <div className="text-xs text-[#0D2B5E]/60">{role.description}</div>
              </div>
              {dirty[role.id] && (
                <button onClick={() => save(role)} className="text-xs bg-[#1565C0] text-white px-3 py-1 rounded-sm uppercase tracking-wider font-bold flex items-center gap-2" data-testid={`save-role-${role.name}`}>
                  <Save size={12}/> Save
                </button>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-white text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
                  <tr>
                    <th className="text-left px-4 py-2 sticky left-0 bg-white">Module</th>
                    {ACTIONS.map((a) => <th key={a} className="px-3 py-2 text-center">{a}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {MODULES.map((m, idx) => (
                    <tr key={m} className={idx % 2 === 0 ? "bg-white" : "bg-[#F5F5F5]/40"}>
                      <td className="px-4 py-2 sticky left-0 bg-inherit font-medium capitalize">{m.replace("_", " ")}</td>
                      {ACTIONS.map((a) => (
                        <td key={a} className="text-center px-3 py-2">
                          <input type="checkbox" disabled={role.is_super_admin} checked={role.is_super_admin || hasAction(role, m, a)} onChange={() => toggle(role, m, a)} data-testid={`perm-${role.name}-${m}-${a}`}/>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
